import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import Button, { ButtonPurpose, ButtonSize } from "shared/Button";
import Dropdown from "shared/@forms/Dropdown";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import TrashButtonIcon from "shared/assets/Trash_Btn.svg";
import styles from "./MediaDisclosuresSection.module.css";

const noop = () => {};

export const PLAYBACK_ERROR_MESSAGE = "Couldn't play this file.";

// pause() during a pending play() rejects with AbortError; that's an
// interruption (e.g. another row was played), not a playback failure.
const isPlaybackInterruption = (error) => error?.name === "AbortError";

// Each assigned row needs its own id: play state, selection and drag/drop
// are all keyed by it. Disclosures loaded from the API carry no id, so
// without this every saved row shared `undefined` — pressing play toggled
// every row, and picking a disclosure changed every row.
let assignmentIdCounter = 0;
export const newDisclosureAssignmentId = () => {
  assignmentIdCounter += 1;
  return `media-disclosure-${Date.now()}-${assignmentIdCounter}`;
};

export const withDisclosureAssignmentIds = (assignments) => {
  const seen = new Set();
  return (Array.isArray(assignments) ? assignments : []).map((assignment) => {
    const id = assignment?.id;
    if (id && !seen.has(id)) {
      seen.add(id);
      return assignment;
    }
    const next = { ...assignment, id: newDisclosureAssignmentId() };
    seen.add(next.id);
    return next;
  });
};

// Audio disclosures can play on any media; video disclosures only on video.
export const isDisclosureAllowedForMedia = (disclosureType, mediaType) =>
  disclosureType !== "Video" || mediaType === "Video";

// Assigned disclosures that don't fit the media type (e.g. after the file
// type is switched from Video to Audio).
export const findIncompatibleDisclosures = (assignments, disclosures, mediaType) =>
  (assignments || []).filter((assignment) => {
    const disclosure = (disclosures || []).find(
      (item) => String(item.id) === String(assignment.disclosureId),
    );
    return (
      disclosure && !isDisclosureAllowedForMedia(disclosure.type, mediaType)
    );
  });

const PlayIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <circle cx="14" cy="14" r="14" />
    <polygon points="11,8 21,14 11,20" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <circle cx="14" cy="14" r="14" />
    <rect x="10" y="8" width="3.5" height="12" rx="1" />
    <rect x="14.5" y="8" width="3.5" height="12" rx="1" />
  </svg>
);

export function MediaDisclosuresSection({
  assignments,
  disclosures,
  mediaType,
  onChange,
}) {
  const [playingAssignmentId, setPlayingAssignmentId] = useState(null);
  const [previewVideoAssignmentId, setPreviewVideoAssignmentId] =
    useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [playbackErrorId, setPlaybackErrorId] = useState(null);
  const audioRef = useRef(null);
  const videoRefs = useRef(new Map());

  const getDisclosureType = (assignment) => {
    const disclosure = disclosures.find(
      (item) =>
        String(item.id) === String(assignment.disclosureId) ||
        item.title === assignment.title,
    );
    return String(assignment.type || disclosure?.type || "Audio").toLowerCase();
  };

  const publishedDisclosures = disclosures.filter(
    (disclosure) =>
      disclosure.status === "Published" &&
      isDisclosureAllowedForMedia(disclosure.type, mediaType),
  );
  const titles = publishedDisclosures.map((disclosure) => disclosure.title);
  const incompatibleIds = new Set(
    findIncompatibleDisclosures(assignments, disclosures, mediaType).map(
      (assignment) => assignment.id,
    ),
  );

  const updateAssignment = (id, partial) => {
    onChange(
      assignments.map((assignment) =>
        assignment.id === id ? { ...assignment, ...partial } : assignment,
      ),
    );
  };

  const handleSelect = (id, title) => {
    setPlaybackErrorId((current) => (current === id ? null : current));
    const disclosure = publishedDisclosures.find(
      (item) => item.title === title,
    );
    updateAssignment(id, {
      disclosureId: disclosure?.id || "",
      title,
      type: disclosure?.type || "Audio",
      previewUrl: disclosure?.previewUrl || "",
    });
  };

  const handleAdd = () => {
    onChange([
      ...assignments,
      {
        id: newDisclosureAssignmentId(),
        disclosureId: "",
        title: "",
        type: "",
        previewUrl: "",
        playAtStart: true,
        playAtEnd: false,
      },
    ]);
  };

  const selectPlaybackPosition = (id, position) => {
    updateAssignment(id, {
      playAtStart: position === "start",
      playAtEnd: position === "end",
    });
  };

  const failPlayback = (id) => {
    setPlayingAssignmentId((current) => (current === id ? null : current));
    setPlaybackErrorId(id);
  };

  const handlePlay = async (assignment) => {
    setPlaybackErrorId(null);
    if (!assignment.previewUrl) {
      setPlaybackErrorId(assignment.id);
      return;
    }

    if (getDisclosureType(assignment) === "video") {
      const video = videoRefs.current.get(assignment.id);
      if (!video) return;

      if (playingAssignmentId === assignment.id && !video.paused) {
        video.pause();
        setPlayingAssignmentId(null);
        return;
      }

      audioRef.current?.pause();
      videoRefs.current.forEach((otherVideo) => {
        if (otherVideo !== video) otherVideo.pause();
      });
      video.src = assignment.previewUrl;
      setPreviewVideoAssignmentId(assignment.id);
      try {
        await video.play();
        setPlayingAssignmentId(assignment.id);
      } catch (error) {
        setPlayingAssignmentId(null);
        if (!isPlaybackInterruption(error)) setPlaybackErrorId(assignment.id);
      }
      return;
    }

    if (playingAssignmentId === assignment.id) {
      audioRef.current?.pause();
      setPlayingAssignmentId(null);
      return;
    }

    if (audioRef.current) audioRef.current.pause();
    videoRefs.current.forEach((video) => video.pause());
    setPreviewVideoAssignmentId(null);
    const audio = new Audio(assignment.previewUrl);
    audioRef.current = audio;
    audio.onended = () => setPlayingAssignmentId(null);
    audio.onpause = () => setPlayingAssignmentId(null);
    // Fires for 403/404s, HTML error pages and undecodable media.
    audio.onerror = () => {
      if (audioRef.current === audio) failPlayback(assignment.id);
    };
    try {
      await audio.play();
      setPlayingAssignmentId(assignment.id);
    } catch (error) {
      setPlayingAssignmentId(null);
      if (!isPlaybackInterruption(error) && audioRef.current === audio) {
        setPlaybackErrorId(assignment.id);
      }
    }
  };

  const handleDrop = (targetId) => {
    if (!draggingId || draggingId === targetId) return;
    const next = [...assignments];
    const sourceIndex = next.findIndex((item) => item.id === draggingId);
    const targetIndex = next.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    onChange(next);
    setDraggingId(null);
  };

  return (
    <section className={styles.card}>
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <h3 className={styles.title}>Disclosures</h3>
          <div className={styles.controlsRow}>
            <Button
              purpose={ButtonPurpose.primary}
              size={ButtonSize.small}
              className={styles.addButton}
              onClick={handleAdd}
              icon={
                <span className={styles.addIconWrap}>
                  <img src={PlusSquareIcon} alt="" className={styles.addIcon} />
                </span>
              }
            >
              Add
            </Button>
          </div>
        </div>
        <div className={styles.divider} />

        {assignments.length === 0 ? (
          <p className={styles.emptyState}>No Disclosures Added</p>
        ) : (
          <ul className={styles.list} aria-label="Assigned disclosures list">
            {assignments.map((assignment) => (
              <li
                key={assignment.id}
                className={styles.listItem}
                draggable
                onDragStart={() => setDraggingId(assignment.id)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleDrop(assignment.id)}
                onDragEnd={() => setDraggingId(null)}
              >
                {getDisclosureType(assignment) === "video" && (
                  <div
                    className={styles.videoPreviewContainer}
                    hidden={previewVideoAssignmentId !== assignment.id}
                  >
                    <video
                      ref={(node) => {
                        if (node) videoRefs.current.set(assignment.id, node);
                        else videoRefs.current.delete(assignment.id);
                      }}
                      className={styles.videoPreview}
                      controls
                      src={
                        previewVideoAssignmentId === assignment.id
                          ? assignment.previewUrl
                          : undefined
                      }
                      onPlay={() => {
                        setPlayingAssignmentId(assignment.id);
                        setPlaybackErrorId((current) =>
                          current === assignment.id ? null : current,
                        );
                      }}
                      onPause={() =>
                        setPlayingAssignmentId((current) =>
                          current === assignment.id ? null : current,
                        )
                      }
                      onEnded={() => setPlayingAssignmentId(null)}
                      onError={() => {
                        if (previewVideoAssignmentId === assignment.id) {
                          failPlayback(assignment.id);
                        }
                      }}
                    />
                  </div>
                )}
                <div className={styles.assignmentTopRow}>
                  <div className={styles.selectorGroup}>
                    <Dropdown
                      className={styles.dropdown}
                      value={assignment.title}
                      options={titles}
                      placeholder="Select"
                      onChange={(title) => handleSelect(assignment.id, title)}
                      chevronClassName={styles.dropdownChevron}
                      chevronOpenClassName={styles.dropdownChevronOpen}
                      buttonBackgroundColor="#ffffff"
                      menuBackgroundColor="#283b4d"
                    />
                    <button
                      type="button"
                      className={styles.playButton}
                      disabled={
                        !assignment.previewUrl &&
                        !assignment.title &&
                        !assignment.disclosureId
                      }
                      onClick={() => handlePlay(assignment)}
                      aria-label={
                        playingAssignmentId === assignment.id
                          ? `Pause ${assignment.title || "disclosure"}`
                          : `Play ${assignment.title || "disclosure"}`
                      }
                    >
                      {playingAssignmentId === assignment.id ? (
                        <PauseIcon />
                      ) : (
                        <PlayIcon />
                      )}
                    </button>
                  </div>

                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() =>
                        onChange(
                          assignments.filter(
                            (item) => item.id !== assignment.id,
                          ),
                        )
                      }
                      aria-label={`Remove ${assignment.title || "disclosure"}`}
                    >
                      <img src={TrashButtonIcon} alt="" />
                    </button>
                    <div
                      className={styles.dragHandle}
                      aria-label="Drag to reorder"
                    >
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>

                {playbackErrorId === assignment.id && (
                  <p className={styles.playbackError} role="alert">
                    {PLAYBACK_ERROR_MESSAGE}
                  </p>
                )}

                {incompatibleIds.has(assignment.id) && (
                  <p className={styles.typeWarning} role="alert">
                    Video disclosures can only be added to video media. Remove
                    this disclosure or change the file type to Video.
                  </p>
                )}

                <div className={styles.playAtRow}>
                  <span className={styles.playAtLabel}>Play Disclosure At</span>
                  <div
                    className={styles.positionButtons}
                    role="radiogroup"
                    aria-label={`Play Disclosure At for ${
                      assignment.title || "disclosure"
                    }`}
                  >
                    <label
                      className={`${styles.positionButton} ${
                        assignment.playAtStart
                          ? styles.positionButtonActive
                          : ""
                      }`}
                    >
                      <input
                        className={styles.positionRadio}
                        type="radio"
                        name={`play-at-${assignment.id}`}
                        value="start"
                        checked={Boolean(assignment.playAtStart)}
                        onChange={() =>
                          selectPlaybackPosition(assignment.id, "start")
                        }
                      />
                      <span>Start</span>
                    </label>
                    <label
                      className={`${styles.positionButton} ${
                        assignment.playAtEnd ? styles.positionButtonActive : ""
                      }`}
                    >
                      <input
                        className={styles.positionRadio}
                        type="radio"
                        name={`play-at-${assignment.id}`}
                        value="end"
                        checked={Boolean(assignment.playAtEnd)}
                        onChange={() =>
                          selectPlaybackPosition(assignment.id, "end")
                        }
                      />
                      <span>End</span>
                    </label>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

MediaDisclosuresSection.propTypes = {
  assignments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      disclosureId: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      previewUrl: PropTypes.string,
      playAtStart: PropTypes.bool,
      playAtEnd: PropTypes.bool,
    }),
  ),
  disclosures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      type: PropTypes.string,
      previewUrl: PropTypes.string,
    }),
  ),
  mediaType: PropTypes.string,
  onChange: PropTypes.func,
};

MediaDisclosuresSection.defaultProps = {
  assignments: [],
  disclosures: [],
  mediaType: "Audio",
  onChange: noop,
};

export default MediaDisclosuresSection;
